import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/config/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { resolveActiveBrandKitId } from '@/lib/identity/resolveActiveBrandKitId';

interface UseActiveWorkspaceIdOptions {
  enabled?: boolean;
  staleTime?: number;
}

/**
 * THE reader of the RESOLVED workspace id for workspace-scoped data hooks.
 *
 * Never key this query as `['active-workspace-pointer', userId]`. That key
 * belongs to `useBrandKits`, which caches the RAW `user_profiles` pointer: null
 * for anyone who never switched workspace, refetched on every mount and window
 * focus. A data hook that registered its own resolved id under the same key was
 * overwritten with that null, read it as "no workspace", and the page answered
 * "No events yet" and "That event is not in this workspace" for an account that
 * held 17 events. A minute later its own refetch put the id back, which is why
 * the list reappeared (round 5 lane R1, test account, 2 of 2 runs).
 *
 * `useReachEntitlement` already moved to this key for the same reason
 * (2026-09-29). `switchWorkspace` publishes the new id here directly.
 */
export function useActiveWorkspaceId({
  enabled = true,
  staleTime = 5 * 60 * 1000,
}: UseActiveWorkspaceIdOptions = {}) {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.brandKits.effectiveWorkspace(user?.id),
    queryFn: () => resolveActiveBrandKitId(),
    enabled: enabled && Boolean(user?.id),
    staleTime,
  });
}
